import React, { createContext, useContext, useReducer } from 'react';

import __noop from '@atlaskit/ds-lib/noop';

import type {
	ColorAndComponents,
	ColorMode,
	Iconography,
	Typography,
	VisualRefreshMode,
} from '../example/types';

export type UserPreferences = {
	/**
	 * Site-wide color mode preference.
	 */
	colorMode: ColorMode | null;
	/**
	 * Code example color mode preference.
	 */
	exampleColorMode: ColorMode | null;
	/**
	 * Code example typography preference.
	 */
	exampleTypography: Typography | null;
	/**
	 * Code example iconography preference.
	 */
	exampleIconography: Iconography | null;
	/**
	 * Code example color and component preference.
	 */
	exampleColorAndComponents: ColorAndComponents | null;
	/**
	 * Sync example user preferences across all examples.
	 */
	exampleSyncSettings: 'true' | 'false' | null;
	/**
	 * Code example visual refresh mode preference.
	 */
	exampleVisualRefreshMode: VisualRefreshMode | null;
};

const defaultUserPreferences: UserPreferences = {
	colorMode: null,
	exampleColorMode: null,
	exampleTypography: null,
	exampleIconography: null,
	exampleColorAndComponents: null,
	exampleSyncSettings: null,
	exampleVisualRefreshMode: null,
};

type UserPreferencesContextProps = {
	userPreferences: UserPreferences;
	setUserPreferences: (preferences: Partial<UserPreferences>) => void;
};

const UserPreferencesContext = createContext<UserPreferencesContextProps>({
	userPreferences: defaultUserPreferences,
	setUserPreferences: __noop,
});

export const useUserPreferences = () => {
	const { userPreferences, setUserPreferences } = useContext(UserPreferencesContext);

	return { userPreferences, setUserPreferences };
};

export const getUserPreferencesLocalStorageKey = (preferenceName: string) => `up.${preferenceName}`;

/**
 * Reads user preferences from local storage and returns the initial state
 * for initial render.
 */
export const getInitialUserPreferences = () => {
	let initialUserPreferences: UserPreferences = { ...defaultUserPreferences };

	// SSR-safe check
	if (typeof window !== 'undefined') {
		Object.keys(initialUserPreferences).forEach((key) => {
			const storedValue = localStorage.getItem(getUserPreferencesLocalStorageKey(key));
			if (storedValue !== null) {
				initialUserPreferences[key as keyof UserPreferences] = storedValue as any;
			}
		});
	}

	return initialUserPreferences;
};

type UserPreferencesProviderProps = {
	children?: React.ReactNode;
};

type Action = {
	type: 'SET_USER_PREFERENCES';
	payload: Partial<UserPreferences>;
};

const userPreferenceReducer = (state: UserPreferences, action: Action): UserPreferences => {
	switch (action.type) {
		case 'SET_USER_PREFERENCES':
			const newState = { ...state, ...action.payload };
			Object.entries(action.payload).forEach(([preferenceName, value]) => {
				if (value === null) {
					localStorage.removeItem(getUserPreferencesLocalStorageKey(preferenceName));
				} else {
					localStorage.setItem(getUserPreferencesLocalStorageKey(preferenceName), value.toString());
				}
			});
			return newState;
		default:
			return state;
	}
};

/**
 * __User preferences provider__
 *
 * Provides access to user preferences which are stored within, and accessed from local storage.
 */
export const UserPreferencesProvider = ({ children }: UserPreferencesProviderProps) => {
	const [userPreferences, dispatch] = useReducer(
		userPreferenceReducer,
		{},
		getInitialUserPreferences,
	);

	const setUserPreferences = (preferences: Partial<UserPreferences>) => {
		dispatch({ type: 'SET_USER_PREFERENCES', payload: preferences });
	};

	return (
		<UserPreferencesContext.Provider
			value={{
				userPreferences,
				setUserPreferences,
			}}
		>
			{children}
		</UserPreferencesContext.Provider>
	);
};
